// 主题模块服务层

import { themeApi } from "../api";
import { Theme } from "../types";

// =============================================================
// Store Dispatcher 注入（与 homeDisplay 同样的模式）
// -------------------------------------------------------------
// 目的：彻底消除 services ↔ stores 循环依赖，同时保留 service.loadTheme()
// 被调用时自动更新 store 状态的既有行为。service 完全不 import store，
// 改由 stores/index.ts 在模块加载完成时调用 `setThemeStoreDispatcher`
// 注入状态访问器。
// =============================================================
interface ThemeStoreDispatcher {
  getCache: (key: string) => Theme | null;
  setCache: (key: string, data: Theme, ttl?: number) => void;
  setTheme: (theme: Theme) => void;
  setThemeLoading: (loading: boolean) => void;
}

let _dispatcher: ThemeStoreDispatcher | null = null;

/** 由 stores/index.ts 在模块加载完成时调用，注入 store 访问器 */
export function setThemeStoreDispatcher(d: ThemeStoreDispatcher): void {
  _dispatcher = d;
}

// 主题服务类
export class ThemeService {
  private static instance: ThemeService;

  static getInstance(): ThemeService {
    if (!ThemeService.instance) {
      ThemeService.instance = new ThemeService();
    }
    return ThemeService.instance;
  }

  // 主题配置服务
  async loadTheme(themeId?: string): Promise<Theme> {
    // 检查缓存（store 未注册时降级为不缓存，直接走 API）
    const cacheKey = `theme_config_${themeId || "default"}`;
    const cached = _dispatcher?.getCache(cacheKey) ?? null;
    if (cached) {
      _dispatcher?.setTheme(cached);
      return cached;
    }

    try {
      _dispatcher?.setThemeLoading(true);

      const data = await themeApi.getTheme(themeId || "default");

      _dispatcher?.setCache(cacheKey, data, 60 * 60 * 1000); // 1小时缓存
      _dispatcher?.setTheme(data);

      return data;
    } catch (error) {
      console.error("主题配置加载失败:", error);
      throw error;
    } finally {
      _dispatcher?.setThemeLoading(false);
    }
  }

  // 获取主题列表
  async loadThemeList(): Promise<Theme[]> {
    const cacheKey = "theme_list";
    const cached = _dispatcher?.getCache(cacheKey) as Theme[] | null;
    if (cached) {
      return cached;
    }

    try {
      const data = await themeApi.getThemeList();

      // 缓存数据：setCache 的签名针对 Theme；列表走 any 通道
      (_dispatcher?.setCache as unknown as
        | ((key: string, data: unknown, ttl?: number) => void)
        | undefined)?.(cacheKey, data, 30 * 60 * 1000);

      return data;
    } catch (error) {
      console.error("主题列表加载失败:", error);
      return [];
    }
  }
}

// 创建单例实例
export const themeService = ThemeService.getInstance();

// 导出便捷方法
export const { loadTheme, loadThemeList } = themeService;
